import {toModulCase} from "./modulCase";

export type ModulTextPart = {node: Text; start: number; end: number};

export function normalizeContentEditableText(root: HTMLElement, normalize?: (text: string) => string) {
  const selection = root.ownerDocument.getSelection();
  const saved = selection?.anchorNode && selection.focusNode &&
    (root.contains(selection.anchorNode) || root.contains(selection.focusNode))
    ? {
      anchorNode: selection.anchorNode, anchorOffset: selection.anchorOffset,
      focusNode: selection.focusNode, focusOffset: selection.focusOffset,
    }
    : null;
  let changed = false;
  const walker = root.ownerDocument.createTreeWalker(root, 4 /* SHOW_TEXT */);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const text = node as Text;
    const normalizer = text.parentElement?.closest(".font-modul") ? toModulCase : normalize;
    if (normalizer) {
      const value = normalizer(text.data);
      if (value !== text.data) {
        if (saved?.anchorNode === text) saved.anchorOffset = normalizer(text.data.slice(0, saved.anchorOffset)).length;
        if (saved?.focusNode === text) saved.focusOffset = normalizer(text.data.slice(0, saved.focusOffset)).length;
        text.data = value;
        changed = true;
      }
    }
  }
  if (saved && changed) {
    selection!.setBaseAndExtent(saved.anchorNode, saved.anchorOffset, saved.focusNode, saved.focusOffset);
  }
}

export function toggleModulFont(root: HTMLElement, selection: Selection): boolean {
  return setModulFont(root, selection);
}

/** Set or remove Modul. Omit enabled to toggle the selected text. */
export function setModulFont(root: HTMLElement, selection: Selection, enabled?: boolean): boolean {
  if (!selection.rangeCount) return false;
  const range = selection.getRangeAt(0);
  if (range.collapsed || !root.contains(range.startContainer) || !root.contains(range.endContainer)) {
    return false;
  }

  const backward = selection.anchorNode === range.endContainer && selection.anchorOffset === range.endOffset;
  const parts: ModulTextPart[] = [];
  const walker = root.ownerDocument.createTreeWalker(root, 4 /* SHOW_TEXT */);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    if (!range.intersectsNode(node)) continue;
    const text = node as Text;
    const start = node === range.startContainer ? range.startOffset : 0;
    const end = node === range.endContainer ? range.endOffset : text.length;
    if (start < end) parts.push({node: text, start, end});
  }

  if (!setModulFontParts(root, parts, enabled)) return false;
  const first = parts[0];
  const last = parts[parts.length - 1];
  if (backward) selection.setBaseAndExtent(last.node, last.end, first.node, first.start);
  else selection.setBaseAndExtent(first.node, first.start, last.node, last.end);
  return true;
}

/** Apply ordered, non-overlapping text parts in one pass. Updates their node offsets. */
export function setModulFontParts(root: HTMLElement, parts: ModulTextPart[], enabled?: boolean): boolean {
  if (!parts.length) return false;
  const allModul = parts.every(({node}) => node.parentElement!.closest(".font-modul"));
  const anyModul = parts.some(({node}) => node.parentElement!.closest(".font-modul"));
  if (enabled === true && allModul || enabled === false && !anyModul) return false;
  const removeModul = enabled === undefined ? allModul : !enabled;
  if (removeModul) {
    const selected = new Set<Text>();
    const wrappers = new Set<HTMLElement>();
    for (const part of parts.toReversed()) {
      if (part.end < part.node.length) part.node.splitText(part.end);
      if (part.start > 0) part.node = part.node.splitText(part.start);
      part.start = 0;
      part.end = part.node.length;
      selected.add(part.node);
      for (let parent = part.node.parentElement; parent && parent !== root; parent = parent.parentElement) {
        if (parent.classList.contains("font-modul")) wrappers.add(parent);
      }
    }

    // Keep Modul on unselected text before removing its shared wrappers.
    // Leave lists, highlights and other inline elements in place.
    const unselected: Text[] = [];
    const walker = root.ownerDocument.createTreeWalker(root, 4 /* SHOW_TEXT */);
    let node: Node | null;
    while ((node = walker.nextNode())) {
      const text = node as Text;
      const wrapper = text.parentElement!.closest<HTMLElement>(".font-modul");
      if (!selected.has(text) && wrapper && wrappers.has(wrapper) && text.length) unselected.push(text);
    }
    for (const text of unselected) {
      const span = root.ownerDocument.createElement("span");
      span.className = "font-modul";
      text.replaceWith(span);
      span.append(text);
    }
    for (const wrapper of wrappers) {
      wrapper.classList.remove("font-modul");
      if (!wrapper.classList.length) wrapper.removeAttribute("class");
      if (wrapper.tagName === "SPAN" && !wrapper.attributes.length) {
        while (wrapper.firstChild) wrapper.before(wrapper.firstChild);
        wrapper.remove();
      }
    }
  } else {
    // Wrap text runs inside their existing elements. A range across list rows
    // must not extract or split the list items themselves.
    for (const part of parts.toReversed()) {
      if (part.node.parentElement?.closest(".font-modul")) continue;
      if (part.end < part.node.length) part.node.splitText(part.end);
      if (part.start > 0) part.node = part.node.splitText(part.start);
      const span = root.ownerDocument.createElement("span");
      span.className = "font-modul";
      part.node.replaceWith(span);
      span.append(part.node);
      normalizeContentEditableText(span);
      part.node = span.firstChild as Text;
      part.start = 0;
      part.end = part.node.length;
    }
  }
  return true;
}
